import { CounterpartyProfile } from "@wingspanhq/wingspan-api-sdk";

/**
 * The subset of a v3 counterparty (Payer or Payee) a display name is derived
 * from — both models share the same `profile` / `email` / `id` fields.
 */
export interface CounterpartyDisplayNameInput {
  profile?: CounterpartyProfile;
  email?: string;
  id?: string;
}

/**
 * Resolves a human-readable display name from a v3 counterparty *entity* (Payer
 * or Payee): `profile.displayName` wins, then the company DBA, then the contact
 * email, and finally the id as a stable fallback.
 *
 * Prefer [[selectorSearchRowName]] wherever a `PayerRow` / `PayeeRow` is
 * reachable. The rows carry `profile.resolvedName`, the name `wsNameV3` resolves
 * server-side at index-populate time, and that is what every list, table and
 * picker in the app renders — naming the same counterparty off the raw entity
 * profile instead makes a detail screen disagree with the list it was opened
 * from. `CounterpartyProfile` has only these two fields, so this derivation has
 * nothing better to fall back on than the email or the id.
 *
 * This is for the cases where no row exists yet or is reachable: a counterparty
 * just returned by a create verb (not indexed yet), or one only ever inlined as
 * an expansion on another resource (`Payable.payee`, `InvoiceSplit.payee`).
 */
export const selectorCounterpartyDisplayName = (
  counterparty: CounterpartyDisplayNameInput
): string => {
  const displayName = counterparty.profile?.displayName?.trim();
  if (displayName) {
    return displayName;
  }

  const company = counterparty.profile?.company?.doingBusinessAs?.trim();
  if (company) {
    return company;
  }

  return counterparty.email || counterparty.id || "";
};
